import React, { memo } from 'react';
import PropTypes from 'prop-types';

import ErrorMessage from '../ErrorMessage';

const ButtonGroup = ({ options, defaultValue, cols, extraClass, colsExtraClass, descriptionExtraClass, optionalLink, multi, errorInModal, onErrorClose, onOptionChange, field, fieldState: { error, invalid } }) => {
  const { name, value, onChange, ...fieldProps } = field;

  const colClasses = (option) =>
    [
      cols > 1 && !options[0].description ? 'col-6' : 'col-12',
      option.wide ? `col-lg-${12 / cols + 2}` : `col-lg-${12 / cols}`,
      option.wide ? `col-xl-${12 / cols + 1}` : '',
      colsExtraClass ? colsExtraClass : '',
    ].join(' ');

  return (
    <div className={`form-cols ${extraClass ? extraClass : ''} form-${cols}-col`}>
      <div className="row g-1 g-lg-2 justify-content-center">
        {options.length > 0 &&
          options.map((option, index) => (
            <div key={`${name}_${index}`} className={colClasses(option)}>
              <label
                className={`btn btn-form ${option.description ? 'btn-form-lg' : ''} ${
                  value.includes(option.value) ? 'active' : ''
                }`}
                htmlFor={`${name}_${index}`}
              >
                {option.description ? (
                  <>
                    <span className="h5">{option.value}</span>
                    <span className={`p ${descriptionExtraClass ? descriptionExtraClass : ''}`}>{option.description}</span>
                  </>
                ) : (
                  option.value
                )}
              </label>
              <input
                {...fieldProps}
                name={multi ? `${name}[]` : name}
                id={`${name}_${index}`}
                className="btn-check"
                aria-invalid={invalid}
                type={multi ? 'checkbox' : 'radio'}
                value={option.value}
                onChange={() => {
                  let { value: val } = option;

                  if (multi) {
                    if (value.includes(val)) {
                      val = value.filter((v) => v !== val);
                    } else {
                      val = [...value, val];
                    }
                  }

                  onChange(val);
                  onOptionChange(option, val.includes(option.value));
                  fieldProps.onBlur();
                }}
                defaultChecked={
                  typeof defaultValue === 'string' ? defaultValue === option.value : defaultValue.includes(option.value)
                }
              />
              {option.highlight && (
                <span className="highlight">{option.highlight}</span>
              )}
            </div>
          ))}
          {optionalLink && (
            <div className={colClasses(optionalLink)}>
              <a className='btn btn-form' href={`${optionalLink.link}`}>{optionalLink.text}</a>
            </div>
          )}
        {error && <ErrorMessage message={error.message} isModal={errorInModal} onClose={() => onErrorClose(field, onOptionChange)} />}
      </div>
    </div>
  );
};

ButtonGroup.propTypes = {
  field: PropTypes.shape().isRequired,
  fieldState: PropTypes.shape().isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      description: PropTypes.string,
      wide: PropTypes.bool,
      highlight: PropTypes.string,
    }),
  ).isRequired,
  optionalLink: PropTypes.shape({
    text: PropTypes.string,
    link: PropTypes.string,
  }),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  cols: PropTypes.number,
  multi: PropTypes.bool,
  errorInModal: PropTypes.bool,
  onErrorClose: PropTypes.func,
  onOptionChange: PropTypes.func,
};

ButtonGroup.defaultProps = {
  cols: 1,
  multi: false,
  errorInModal: false,
  onErrorClose: () => {},
  onOptionChange: () => {},
};

export default memo(ButtonGroup);
